import { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { propertyFilters, propertySortOptions } from '../../data/siteContent';
import { defaultSettings } from '../konstanter/siteConfig';
import { propertyModel } from '../modeller/propertyModel';
import { siteApi } from '../tjenester/siteApi';

const propertiesPerPage = 3;

const parseLeadingNumber = (value) => {
  const digits = String(value || '').match(/\d+/);
  return digits ? Number.parseInt(digits[0], 10) : 0;
};

const parseSizeNumber = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits ? Number.parseInt(digits, 10) : 0;
};

const sortProperties = (properties, sortKey) => {
  const sorted = [...properties];

  switch (sortKey) {
    case 'nyeste':
      return sorted.sort((a, b) => (b.id || 0) - (a.id || 0));
    case 'storrelse-stor':
      return sorted.sort((a, b) => parseSizeNumber(b.size) - parseSizeNumber(a.size));
    case 'storrelse-liten':
      return sorted.sort((a, b) => parseSizeNumber(a.size) - parseSizeNumber(b.size));
    case 'navn':
      return sorted.sort((a, b) => a.name.localeCompare(b.name, 'nb'));
    default:
      return sorted.sort((a, b) => (a.id || 0) - (b.id || 0));
  }
};

export function useHomePage() {
  const [siteData, setSiteData] = useState({
    settings: defaultSettings,
    properties: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilter = searchParams.get('filter');
  const initialStart = Number.parseInt(searchParams.get('start') ?? '0', 10);
  const initialSort = searchParams.get('sort');
  const [activeFilter, setActiveFilter] = useState(
    propertyFilters.some((filter) => filter.key === initialFilter) ? initialFilter : 'alle'
  );
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') ?? '');
  const [activeType, setActiveType] = useState(searchParams.get('type') ?? 'alle');
  const [minUnits, setMinUnits] = useState(() => {
    const parsed = Number.parseInt(searchParams.get('units') ?? '0', 10);
    return Number.isNaN(parsed) ? 0 : Math.max(0, parsed);
  });
  const [sortKey, setSortKey] = useState(
    propertySortOptions.some((option) => option.key === initialSort) ? initialSort : 'standard'
  );
  const [activeStartIndex, setActiveStartIndex] = useState(Number.isNaN(initialStart) ? 0 : Math.max(0, initialStart));

  useEffect(() => {
    let isMounted = true;

    siteApi
      .getPublicSiteContent()
      .then((data) => {
        if (!isMounted) {
          return;
        }

        setSiteData({
          settings: {
            ...defaultSettings,
            ...data.settings,
          },
          properties: data.properties.map(propertyModel.mapWithSlug),
        });
        setLoadError('');
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }

        setLoadError(error.message);
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const availableTypes = useMemo(() => {
    const types = siteData.properties.map((property) => property.type).filter(Boolean);
    return Array.from(new Set(types)).sort((a, b) => a.localeCompare(b, 'nb'));
  }, [siteData.properties]);

  const portfolioStats = useMemo(() => {
    const totalProperties = siteData.properties.length;
    const availableNow = siteData.properties.filter((property) => property.availability === 'ledig').length;

    return (siteData.settings.portfolioStats || []).map((stat) => {
      const normalizedLabel = String(stat.label || '').toLowerCase();

      if (normalizedLabel.includes('eiendom')) {
        return { ...stat, value: String(totalProperties) };
      }

      if (normalizedLabel.includes('ledig')) {
        return { ...stat, value: String(availableNow).padStart(2, '0') };
      }

      return stat;
    });
  }, [siteData.properties, siteData.settings.portfolioStats]);

  const filteredProperties = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    const matches = siteData.properties.filter((property) => {
      if (activeFilter !== 'alle' && property.availability !== activeFilter) {
        return false;
      }

      if (activeType !== 'alle' && property.type !== activeType) {
        return false;
      }

      if (minUnits > 0 && parseLeadingNumber(property.rooms) < minUnits) {
        return false;
      }

      if (normalizedSearch) {
        const haystack = `${property.name} ${property.address}`.toLowerCase();
        if (!haystack.includes(normalizedSearch)) {
          return false;
        }
      }

      return true;
    });

    return sortProperties(matches, sortKey);
  }, [siteData.properties, activeFilter, activeType, minUnits, searchTerm, sortKey]);

  const canBrowseProperties = filteredProperties.length > propertiesPerPage;
  const normalizedStartIndex = filteredProperties.length === 0 ? 0 : activeStartIndex % filteredProperties.length;
  const visibleProperties =
    filteredProperties.length <= propertiesPerPage
      ? filteredProperties
      : Array.from({ length: propertiesPerPage }, (_, offset) => {
          const propertyIndex = (normalizedStartIndex + offset) % filteredProperties.length;
          return filteredProperties[propertyIndex];
        });

  useEffect(() => {
    const params = {
      filter: activeFilter,
      start: String(normalizedStartIndex),
    };

    if (searchTerm.trim()) {
      params.q = searchTerm.trim();
    }

    if (activeType !== 'alle') {
      params.type = activeType;
    }

    if (minUnits > 0) {
      params.units = String(minUnits);
    }

    if (sortKey !== 'standard') {
      params.sort = sortKey;
    }

    setSearchParams(params, { replace: true });
  }, [activeFilter, activeType, minUnits, normalizedStartIndex, searchTerm, sortKey, setSearchParams]);

  useEffect(() => {
    if (location.hash === '#eiendommer') {
      const propertySection = document.getElementById('eiendommer');

      if (propertySection) {
        propertySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [location.hash]);

  const goToPreviousPage = () => {
    if (!canBrowseProperties) {
      return;
    }

    setActiveStartIndex((currentIndex) => {
      if (currentIndex <= 0) {
        return filteredProperties.length - 1;
      }

      return currentIndex - 1;
    });
  };

  const goToNextPage = () => {
    if (!canBrowseProperties) {
      return;
    }

    setActiveStartIndex((currentIndex) => {
      if (currentIndex >= filteredProperties.length - 1) {
        return 0;
      }

      return currentIndex + 1;
    });
  };

  const handleFilterChange = (filterKey) => {
    setActiveFilter(filterKey);
    setActiveStartIndex(0);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setActiveStartIndex(0);
  };

  const handleTypeChange = (value) => {
    setActiveType(value);
    setActiveStartIndex(0);
  };

  const handleUnitsChange = (value) => {
    const parsed = Number.parseInt(value, 10);
    setMinUnits(Number.isNaN(parsed) ? 0 : Math.max(0, parsed));
    setActiveStartIndex(0);
  };

  const handleSortChange = (value) => {
    setSortKey(value);
    setActiveStartIndex(0);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setActiveType('alle');
    setMinUnits(0);
    setSortKey('standard');
    setActiveFilter('alle');
    setActiveStartIndex(0);
  };

  return {
    activeFilter,
    activeType,
    availableTypes,
    canBrowseProperties,
    filteredProperties,
    goToNextPage,
    goToPreviousPage,
    handleFilterChange,
    handleSearchChange,
    handleSortChange,
    handleTypeChange,
    handleUnitsChange,
    isLoading,
    loadError,
    minUnits,
    normalizedStartIndex,
    resetFilters,
    searchTerm,
    portfolioStats,
    settings: siteData.settings,
    sortKey,
    visibleProperties,
  };
}